import { ref } from 'vue'
import { defineStore } from 'pinia'

import type { IDataFilters } from '@/types/inputs/IDataFilters'
import type { IAction } from '@/types/api/IAction'

export const useUiStore = defineStore('ui', () => {
  /**
   * There's a fireworks animation that we can play just by toggling this.
   * Just call startFireworks() to start the animation, and it will stop after
   * the timeout set in showFireworksTimeout.
   */
  const storeShowFireworks = ref(false)
  const showFireworksTimeout = ref(6000)
  const storeStartFireworks = () => {
    storeShowFireworks.value = true
    setTimeout(() => {
      storeShowFireworks.value = false
    }, showFireworksTimeout.value)
  }

  const storeActionsInProgress = ref<IAction[]>([])
  const storeDynamicIndexesInProgress = ref<number[]>([])
  const storeActionsInError = ref<IAction[]>([])
  const storeActionsInSuccess = ref<IAction[]>([])

  const storeFilterState = ref<IDataFilters>({
    selectedOperationalBase: { id: 0, name: '' },
    selectedDate: '',
  })

  const storeNavDrawerIsOpen = ref(false)
  function storeToggleNavDrawerIsOpen() {
    storeNavDrawerIsOpen.value = !storeNavDrawerIsOpen.value
  }

  function storeAddToActionsInProgress(action: IAction, dynamicIndex?: number) {
    if (!storeActionIsInProgress(action)) {
      storeActionsInProgress.value.push(action)
    }
    // dynamicIndex can be 0, which would evaluate to false if we
    // just did an if (dynamicIndex) check.
    if (Number.isInteger(dynamicIndex) && !storeDynamicIndexesInProgress.value.includes(dynamicIndex!)) {
      storeDynamicIndexesInProgress.value.push(dynamicIndex!)
    }
  }

  function storeRemoveFromActionsInProgress(action: IAction, dynamicIndex?: number) {
    storeActionsInProgress.value = storeActionsInProgress.value.filter(a => a.id !== action.id)
    if (Number.isInteger(dynamicIndex)) {
      storeDynamicIndexesInProgress.value = storeDynamicIndexesInProgress.value.filter(i => i !== dynamicIndex)
    }
  }

  function storeActionIsInProgress(action: IAction, dynamicIndex?: number) {
    // dynamicIndex can be 0, which would evaluate to false if we
    // just did an if (dynamicIndex) check.
    if (Number.isInteger(dynamicIndex)) {
      return storeActionsInProgress.value.some(a => a.id === action.id)
        && storeDynamicIndexesInProgress.value.includes(dynamicIndex!)
    }
    else {
      return storeActionsInProgress.value.some(a => a.id === action.id)
    }
  }

  function storeActionIsInError(action: IAction, dynamic?: boolean) {
    if (dynamic) {
      return storeActionsInError.value.some(a => a.id.includes(action.id))
    }
    return storeActionsInError.value.some(a => a.id === action.id)
  }

  function storeActionIsInSuccess(action: IAction, dynamic?: boolean) {
    if (dynamic) {
      return storeActionsInSuccess.value.some(a => a.id.includes(action.id))
    }
    return storeActionsInSuccess.value.some(a => a.id === action.id)
  }

  function storeAddToActionsInError(action: IAction, e: any) {
    /**
     * We check to see if 'e' is a response from the server
     * with an error message. If it is, we replace the default
     * error message with the one from the server.
     */
    if (e.response) {
      action.errorMessage = e.response.data.message
    }

    if (!storeActionIsInError(action)) {
      storeActionsInError.value.push(action)
    }
  }

  function storeAddToActionsInSuccess(action: IAction, successMessage: string | null = null) {
    if (successMessage) {
      action.successMessage = successMessage
    }

    if (!storeActionIsInSuccess(action)) {
      storeActionsInSuccess.value.push(action)
    }
  }

  function storeResetActionsInError() {
    storeActionsInError.value = []
  }

  function storeResetActionsInSuccess() {
    storeActionsInSuccess.value = []
  }

  function storeResetActionsInProgress() {
    storeActionsInProgress.value = []
  }

  function storeResetUi() {
    storeResetActionsInError()
    storeResetActionsInSuccess()
    storeResetActionsInProgress()
  }

  function storeGetActionErrorMessage(action: IAction, dynamic?: boolean) {
    if (dynamic) {
      return storeActionsInError.value.find(a => a.id.includes(action.id))?.errorMessage
    }
    return storeActionsInError.value.find(a => a.id === action.id)?.errorMessage
  }

  function storeGetActionSuccessMessage(action: IAction, dynamic?: boolean) {
    if (dynamic) {
      return storeActionsInSuccess.value.find(a => a.id.includes(action.id))?.successMessage
    }
    return storeActionsInSuccess.value.find(a => a.id === action.id)?.successMessage
  }

  function storeSetFilterState(state: IDataFilters) {
    storeFilterState.value = state
  }

  return {
    storeShowFireworks,
    storeStartFireworks,
    storeAddToActionsInProgress,
    storeRemoveFromActionsInProgress,
    storeActionIsInProgress,
    storeActionIsInError,
    storeActionIsInSuccess,
    storeAddToActionsInError,
    storeAddToActionsInSuccess,
    storeResetActionsInError,
    storeResetActionsInSuccess,
    storeResetActionsInProgress,
    storeResetUi,
    storeActionsInProgress,
    storeDynamicIndexesInProgress,
    storeActionsInError,
    storeActionsInSuccess,
    storeNavDrawerIsOpen,
    storeToggleNavDrawerIsOpen,
    storeGetActionErrorMessage,
    storeGetActionSuccessMessage,
    storeSetFilterState,
    storeFilterState,
  }
})
